import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import html2canvas from 'html2canvas';

/**
 * Discriminated union describing the lifecycle of a capture (screenshot or video).
 *
 * This service ONLY captures — it does not upload. On success it emits the raw
 * Blob(s); the host (app.component) is responsible for uploading them (e.g. via a
 * @brafton/feedback-api signed URL).
 */
export type CaptureEvent =
  | { kind: 'countdown'; secondsRemaining: number }
  | { kind: 'preparing' }
  | { kind: 'recording' }
  | { kind: 'warning'; risks: string[]; message: string }
  | { kind: 'screenshot'; blob: Blob; dataUrl: string }
  | { kind: 'video'; blob: Blob; posterBlob: Blob | null; durationMs: number; mimeType: string }
  | { kind: 'cancelled' }
  | { kind: 'error'; message: string };

/**
 * Self-contained screen-capture service for the feedback widget.
 *
 * Primary (high fidelity) path: the Screen Capture API (`getDisplayMedia`) for
 * both single-frame screenshots and screen recordings. Fallback (screenshot
 * only): `html2canvas` rasterising the current viewport WITHOUT a proxy — note
 * this cannot render cross-origin host images that lack CORS headers.
 *
 * The service owns its own offscreen <video> element and never reaches into the
 * host component's DOM. It emits Blobs; uploading is the host's concern.
 */
@Injectable()
export class ScreenshotCapture implements OnDestroy {
  /** True only when the Screen Capture API is usable (supported + secure context). */
  public readonly mediaCaptureSupported =
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getDisplayMedia === 'function' &&
    typeof window !== 'undefined' &&
    window.isSecureContext;

  /**
   * Whether native screen capture should be offered. Drives the capture modal
   * (Video/Delay vs. manual upload), the screenshot path, and the warning. False
   * when the Screen Capture API isn't available (older/embedded browsers, non-secure
   * contexts) — then we fall back to html2canvas + manual upload.
   */
  public get nativeCaptureAvailable(): boolean {
    return this.mediaCaptureSupported;
  }

  /** Hard cap so a forgotten recording cannot run forever. */
  private readonly maxVideoDurationMs = 60_000;

  private readonly displayMediaOptions: DisplayMediaStreamOptions = {
    video: { displaySurface: 'browser' } as MediaTrackConstraints,
    audio: false,
  };

  private activeStream: MediaStream | null = null;
  private activeVideoElement: HTMLVideoElement | null = null;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private videoStopTimer: ReturnType<typeof setTimeout> | null = null;
  private mediaRecorder: MediaRecorder | null = null;

  ngOnDestroy(): void {
    this.teardown();
  }

  // ------------------------------------------------------------------ Screenshot

  /**
   * Capture a single screenshot of the current viewport; emits a `screenshot`
   * Blob (+ a data URL for preview). The caller chooses the timing: `delaySeconds`
   * (0 = immediate / "take it now"; >0 runs a countdown before grabbing the frame).
   */
  captureScreenshot(delaySeconds = 0): Observable<CaptureEvent> {
    const events = new Subject<CaptureEvent>();
    if (this.nativeCaptureAvailable) {
      void this.captureScreenshotViaDisplayMedia(events, delaySeconds);
    } else {
      this.captureScreenshotViaHtmlCanvas(events, delaySeconds);
    }
    return events.asObservable();
  }

  private async captureScreenshotViaDisplayMedia(
    events: Subject<CaptureEvent>,
    delaySeconds: number,
  ): Promise<void> {
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia(this.displayMediaOptions);
    } catch (error) {
      // Native capture is supported here, so we do NOT fall back to html2canvas
      // (that path is only for browsers without getDisplayMedia). Dismissing the
      // screen/window picker is a cancellation; anything else is a real error.
      this.teardown();
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        events.next({ kind: 'cancelled' });
      } else {
        events.next({ kind: 'error', message: 'Could not start screen capture. Please try again.' });
      }
      events.complete();
      return;
    }

    this.activeStream = stream;

    let videoElement: HTMLVideoElement;
    try {
      videoElement = await this.attachStream(stream);
    } catch {
      this.teardown();
      events.next({ kind: 'error', message: 'Could not read the shared screen. Please try again.' });
      events.complete();
      return;
    }

    if (delaySeconds > 0) {
      await this.runCountdown(delaySeconds, events);
    }

    // Let the host hide the widget UI before we grab the frame.
    events.next({ kind: 'preparing' });
    await this.delay(300);

    try {
      const canvas = this.drawFrameFromVideo(videoElement);
      await this.emitScreenshot(canvas, events);
    } catch {
      events.next({ kind: 'error', message: 'There was an error taking your screenshot. Please try again.' });
      events.complete();
    } finally {
      this.teardown();
    }
  }

  private captureScreenshotViaHtmlCanvas(events: Subject<CaptureEvent>, delaySeconds: number): void {
    const rasterise = () => {
      events.next({ kind: 'preparing' });
      // Defer so the host can hide its own overlay before we rasterise.
      window.setTimeout(() => {
        const devicePixelRatio = window.devicePixelRatio || 1;
        const viewportWidth = document.documentElement.clientWidth;
        const viewportHeight = document.documentElement.clientHeight;

        // Warn the reporter when the in-page screenshot may be inaccurate, so they
        // can fall back to a manual upload. Always warns when native screen capture
        // isn't available; otherwise only when the page uses features html2canvas
        // captures poorly (Elementor, parallax, cross-origin images).
        const warning = this.buildCaptureWarning();
        if (warning) {
          events.next({ kind: 'warning', risks: warning.risks, message: warning.message });
        }

        // Render ONLY the visible viewport. We size the canvas to the viewport and
        // shift the render up/left by the scroll offset via scrollX/scrollY — this
        // is what actually lands on the scrolled-to section. (html2canvas 1.4.1's
        // x/y crop is unreliable, and rendering the full page then cropping breaks
        // on parallax/scroll-animated pages where the clone's layout doesn't match.)
        html2canvas(document.documentElement, {
          logging: false,
          useCORS: true,
          scale: devicePixelRatio,
          width: viewportWidth,
          height: viewportHeight,
          windowWidth: viewportWidth,
          windowHeight: viewportHeight,
          scrollX: -window.scrollX,
          scrollY: -window.scrollY,
          ignoreElements: (element) => this.isWidgetChrome(element),
          onclone: (clonedDocument) => this.prepareCloneForCapture(clonedDocument),
        })
          .then((canvas) => this.emitScreenshot(canvas, events))
          .catch(() => {
            events.next({
              kind: 'error',
              message:
                'There was an error taking your screenshot. Please try again. If this persists, contact your Brafton team.',
            });
            events.complete();
          });
      }, 300);
    };

    if (delaySeconds > 0) {
      void this.runCountdown(delaySeconds, events).then(rasterise);
    } else {
      rasterise();
    }
  }

  // ----------------------------------------------------------------------- Video

  /**
   * Start a screen recording. Recording continues until {@link stopVideo} is
   * called, the user stops sharing, or the safety cap is reached — then a `video`
   * event is emitted with the recording Blob and a poster Blob (the first frame).
   * Requires the Screen Capture API (there is no html2canvas equivalent).
   */
  captureVideo(): Observable<CaptureEvent> {
    const events = new Subject<CaptureEvent>();
    if (!this.nativeCaptureAvailable || typeof MediaRecorder === 'undefined') {
      events.next({ kind: 'error', message: 'Screen recording is not supported in this browser.' });
      events.complete();
      return events.asObservable();
    }
    void this.startVideoRecording(events);
    return events.asObservable();
  }

  /** Stop an in-progress recording; the `video` event follows. */
  stopVideo(): void {
    if (this.videoStopTimer !== null) {
      clearTimeout(this.videoStopTimer);
      this.videoStopTimer = null;
    }
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
  }

  private async startVideoRecording(events: Subject<CaptureEvent>): Promise<void> {
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia(this.displayMediaOptions);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        events.next({ kind: 'cancelled' });
      } else {
        events.next({ kind: 'error', message: 'Screen recording was cancelled or blocked.' });
      }
      events.complete();
      return;
    }

    this.activeStream = stream;

    // Let the host hide its UI before the first frame is grabbed for the poster.
    events.next({ kind: 'preparing' });

    // Grab the first frame as a poster (best-effort) before recording.
    let posterBlob: Blob | null = null;
    try {
      const videoElement = await this.attachStream(stream);
      posterBlob = await this.canvasToBlob(this.drawFrameFromVideo(videoElement));
    } catch {
      posterBlob = null;
    }

    const recordedChunks: Blob[] = [];
    const startedAt = Date.now();
    const mimeType = this.pickVideoMimeType();
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    this.mediaRecorder = recorder;

    recorder.addEventListener('dataavailable', (event) => {
      if (event.data.size > 0) {
        recordedChunks.push(event.data);
      }
    });
    recorder.addEventListener('stop', () => {
      const durationMs = Date.now() - startedAt;
      const resolvedType = recorder.mimeType || 'video/webm';
      const recording = new Blob(recordedChunks, { type: resolvedType });
      this.stopStream();
      events.next({ kind: 'video', blob: recording, posterBlob, durationMs, mimeType: resolvedType });
      events.complete();
    });

    // If the user stops sharing via the browser's own control, finish the clip.
    const [videoTrack] = stream.getVideoTracks();
    if (videoTrack) {
      videoTrack.addEventListener('ended', () => this.stopVideo());
    }

    this.videoStopTimer = setTimeout(() => this.stopVideo(), this.maxVideoDurationMs);
    recorder.start();
    // Recording has begun — the host can now reveal the Stop control + countdown.
    events.next({ kind: 'recording' });
  }

  // --------------------------------------------------------------------- Helpers

  /** Emit the screenshot Blob (+ a data URL for an immediate preview). */
  private async emitScreenshot(canvas: HTMLCanvasElement, events: Subject<CaptureEvent>): Promise<void> {
    const dataUrl = canvas.toDataURL('image/png');
    const blob = await this.canvasToBlob(canvas);
    if (!blob) {
      events.next({ kind: 'error', message: 'The screenshot could not be encoded.' });
      events.complete();
      return;
    }
    events.next({ kind: 'screenshot', blob, dataUrl });
    events.complete();
  }

  /** Promisified canvas.toBlob; resolves null if encoding is unavailable/fails. */
  private canvasToBlob(canvas: HTMLCanvasElement, type = 'image/png'): Promise<Blob | null> {
    return new Promise<Blob | null>((resolve) => {
      if (typeof canvas.toBlob !== 'function') {
        resolve(null);
        return;
      }
      canvas.toBlob((blob) => resolve(blob), type);
    });
  }

  /** Build a hidden <video>, attach the stream, and wait until it can paint. */
  private attachStream(stream: MediaStream): Promise<HTMLVideoElement> {
    const videoElement = document.createElement('video');
    videoElement.muted = true;
    videoElement.playsInline = true;
    videoElement.srcObject = stream;
    // The element must be IN the document for the browser to drive its media
    // pipeline. A fully-detached <video> won't reliably reach loadedmetadata or
    // produce frames until the page next renders — which made the capture appear
    // to hang until the user interacted with the page. Park it hidden offscreen.
    videoElement.style.cssText =
      'position:fixed;left:-9999px;top:0;width:1px;height:1px;opacity:0;pointer-events:none;';
    document.body.appendChild(videoElement);
    this.activeVideoElement = videoElement;

    return new Promise<HTMLVideoElement>((resolve, reject) => {
      const start = (): void => {
        videoElement
          .play()
          .then(() => resolve(videoElement))
          .catch(reject);
      };
      videoElement.onerror = () => reject(new Error('video element error'));
      // If metadata is already available the loadedmetadata event won't fire, so
      // start immediately; otherwise wait for it.
      if (videoElement.readyState >= HTMLMediaElement.HAVE_METADATA) {
        start();
      } else {
        videoElement.onloadedmetadata = start;
      }
    });
  }

  /** Draw the captured frame at its native resolution — i.e. what was shared. */
  private drawFrameFromVideo(videoElement: HTMLVideoElement): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('2D canvas context is unavailable');
    }
    context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
    return canvas;
  }

  private runCountdown(seconds: number, events: Subject<CaptureEvent>): Promise<void> {
    return new Promise<void>((resolve) => {
      let secondsRemaining = seconds;
      events.next({ kind: 'countdown', secondsRemaining });
      this.countdownTimer = setInterval(() => {
        secondsRemaining -= 1;
        events.next({ kind: 'countdown', secondsRemaining });
        if (secondsRemaining <= 0) {
          this.clearCountdown();
          resolve();
        }
      }, 1000);
    });
  }

  /**
   * True for widget elements that must NOT appear in the screenshot: our own
   * chrome (the `brafton-feedback` host) AND every feedback marker EXCEPT the
   * active (just-dropped) one. The live page hides non-active markers via the
   * `body.brafton-capturing` CSS rule, but html2canvas renders from a DOM clone
   * that doesn't honour it — so we exclude them explicitly here, keeping only the
   * real marker the reporter just placed (`.brafton-marker.is-active`).
   */
  private isWidgetChrome(element: Element): boolean {
    if (element.tagName && element.tagName.toLowerCase() === 'brafton-feedback') {
      return true;
    }
    if (typeof element.closest === 'function' && element.closest('brafton-feedback')) {
      return true;
    }
    if (
      element.classList?.contains('brafton-marker') &&
      !element.classList.contains('is-active')
    ) {
      return true;
    }
    return false;
  }

  /**
   * Prepare html2canvas's cloned document so it better matches the live page.
   * (#2 mitigation, mainly for Elementor sites.) The clone is detached, so
   * scroll-triggered animations never fire and parallax transforms freeze — this
   * undoes those so already-revealed content stays visible. NOTE: it cannot
   * recover cross-origin images that lack CORS headers (a hard html2canvas limit).
   */
  private prepareCloneForCapture(clonedDocument: Document): void {
    // Reveal Elementor scroll-triggered fade-ins (their IntersectionObserver never
    // fires in the clone, so they'd stay at opacity:0).
    clonedDocument.querySelectorAll<HTMLElement>('.elementor-invisible').forEach((element) => {
      element.classList.remove('elementor-invisible');
      element.style.opacity = '1';
      element.style.visibility = 'visible';
      element.style.animation = 'none';
      element.style.transform = 'none';
    });
    // Mark Elementor containers as lazy-loaded so the `background-image:none`
    // override (applied until `.e-lazyloaded`) is dropped.
    clonedDocument.querySelectorAll('.e-con').forEach((element) => element.classList.add('e-lazyloaded'));
    // Neutralize Motion FX parallax transforms (no JS runs in the clone to keep
    // them synced to scroll, so they'd render mispositioned).
    clonedDocument
      .querySelectorAll<HTMLElement>('.elementor-motion-effects-element, .elementor-motion-effects-container')
      .forEach((element) => {
        element.style.transform = 'none';
      });
  }

  /**
   * Build the warning shown above the screenshot, or null if none is needed.
   * - No native screen capture (getDisplayMedia unavailable): ALWAYS warn — the
   *   reporter should manually screenshot + upload if the render is wrong.
   * - Native capture available but html2canvas used anyway (e.g. test override):
   *   warn only when the page has features html2canvas renders poorly.
   */
  private buildCaptureWarning(): { risks: string[]; message: string } | null {
    if (!this.nativeCaptureAvailable) {
      return {
        risks: ['no native screen capture'],
        message:
          `Native screen capture isn't available in your browser, so the screenshot below may ` +
          `not accurately reflect what you were viewing. If it doesn't, cancel this feedback, ` +
          `take a screenshot manually with your device, then start a new feedback item and upload ` +
          `it below to replace the incorrect one.`,
      };
    }
    const risks = this.detectCaptureRisks();
    if (risks.length > 0) {
      return {
        risks,
        message:
          `This page uses ${this.joinRisks(risks)}, so the screenshot below may not accurately ` +
          `reflect what you were viewing. If it's wrong, cancel, take a screenshot manually with ` +
          `your device, and upload it below to use instead.`,
      };
    }
    return null;
  }

  /**
   * Detect page features that html2canvas captures poorly, so the host can warn
   * the reporter that the screenshot may be inaccurate. Returns a list of
   * human-readable risk labels (empty when the page looks safe to rasterise).
   */
  private detectCaptureRisks(): string[] {
    const risks: string[] = [];
    if (document.querySelector('[data-elementor-type], .elementor-page, .elementor-widget')) {
      risks.push('the Elementor page builder');
    }
    if (
      document.querySelector(
        '.elementor-motion-effects-element, [data-settings*="motion_fx"], [data-settings*="background_motion_fx"]',
      )
    ) {
      risks.push('parallax / scroll effects');
    }
    if (this.hasCrossOriginImages()) {
      risks.push('images from another domain');
    }
    return risks;
  }

  /** Joins risk labels into a natural list: "a", "a and b", "a, b and c". */
  private joinRisks(risks: string[]): string {
    if (risks.length <= 1) {
      return risks[0] ?? '';
    }
    return `${risks.slice(0, -1).join(', ')} and ${risks[risks.length - 1]}`;
  }

  /** True if the page renders <img>s from a different origin (likely uncapturable). */
  private hasCrossOriginImages(): boolean {
    const pageOrigin = window.location.origin;
    return Array.from(document.images).some((image) => {
      try {
        return !!image.currentSrc && new URL(image.currentSrc, pageOrigin).origin !== pageOrigin;
      } catch {
        return false;
      }
    });
  }

  private pickVideoMimeType(): string {
    const candidates = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm', 'video/mp4'];
    for (const candidate of candidates) {
      if (MediaRecorder.isTypeSupported(candidate)) {
        return candidate;
      }
    }
    return '';
  }

  private delay(milliseconds: number): Promise<void> {
    return new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds));
  }

  private clearCountdown(): void {
    if (this.countdownTimer !== null) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  /** Stop and release the capture stream + offscreen video element. */
  private stopStream(): void {
    if (this.activeStream) {
      this.activeStream.getTracks().forEach((track) => track.stop());
      this.activeStream = null;
    }
    if (this.activeVideoElement) {
      this.activeVideoElement.srcObject = null;
      this.activeVideoElement.remove();
      this.activeVideoElement = null;
    }
  }

  /** Full reset: timers, recorder, stream and video element. */
  private teardown(): void {
    this.clearCountdown();
    if (this.videoStopTimer !== null) {
      clearTimeout(this.videoStopTimer);
      this.videoStopTimer = null;
    }
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try {
        this.mediaRecorder.stop();
      } catch {
        // already stopped
      }
    }
    this.mediaRecorder = null;
    this.stopStream();
  }
}
